import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CategoryTreeItem,
  clearExtraDetails,
  dispatch,
  ErrorVariants,
  FilterRoute,
  getAppliedFilters,
  getCatalogConfig,
  getSearchKey,
  getWorkspaceId,
  ProductVariant,
  removeSearchKey,
  showToast,
  updateSelectedProducts,
  useAppSelector,
  useBulkUpdateProductsMutation,
  useCreateUpdateProductMutation,
  useGetProductsMutation,
} from '@zonofi/zono-money-store';
import { isEmpty, isNil } from 'lodash';
import {
  CatalogueView,
  SELLER_WEBAPP_SHARE_ROUTES,
  SortDirection,
} from '../../constants';
import { useDebounceSearch, usePaginatedList } from '../../hooks';
import {
  ChipDataItem,
  ChipFilterSection,
  PriceUpdatedProduct,
  ProductPriceUpdateMap,
} from '../../types';
import { shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';

export const useProductList = () => {
  const location = useLocation();
  const path = location.pathname;

  const { catalogueView } = useAppSelector(getCatalogConfig);
  const searchKey = useAppSelector(getSearchKey);
  const [getProductsList, productsResponse] = useGetProductsMutation();
  const [updateProduct, updateProductResponse] =
    useCreateUpdateProductMutation();
  const [buldUpdateProduct, bulkUpdateProductResponse] =
    useBulkUpdateProductsMutation();
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const appliedFilters = useAppSelector(
    getAppliedFilters(FilterRoute.Product),
    shallowEqual
  );

  const [sharing, setSharing] = useState<boolean>(
    path === SELLER_WEBAPP_SHARE_ROUTES.SHARE_AVAILABILTY ? true : false
  );
  const [filterIndex, setFilterIndex] = useState(0);
  const [successState, setSuccessState] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<
    CategoryTreeItem[]
  >([]);
  const [segmentFilters, setSegmentFilters] = useState<ChipFilterSection[]>([]);
  const prevCategoryTreeRef = useRef<CategoryTreeItem | undefined>();
  const totalRef = useRef<number | null>(null);
  const initialLoadRef = useRef<boolean>(true);

  const [updatedProducts, setUpdatedProducts] = useState<ProductPriceUpdateMap>(
    {}
  );
  const [selectedProducts, setSelectedProducts] = useState<Set<number>>(
    new Set()
  );
  const { debouncedSearchValue } = useDebounceSearch();
  const [showFilter, setShowFilter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState('');

  const categoryTree = useMemo(() => {
    const rootCategory = productsResponse?.data?.categoryTree?.find(
      (category) => category?.categoryCode === '__root_category__'
    );
    if (!rootCategory || !isEmpty(selectedCategories))
      return prevCategoryTreeRef?.current;

    const hasSingleChildWithChildren =
      rootCategory?.children?.length === 1 &&
      rootCategory?.children[0]?.children?.length &&
      rootCategory?.children[0]?.children?.length > 0;

    const hasMultipleChildren =
      rootCategory?.children?.length && rootCategory?.children?.length > 1;

    if (hasSingleChildWithChildren || hasMultipleChildren) {
      const newValue = rootCategory;
      prevCategoryTreeRef.current = newValue;
      return newValue;
    }

    return prevCategoryTreeRef.current;
  }, [productsResponse?.data?.categoryTree, selectedCategories]);

  const memoizedProducts = useMemo(() => {
    return (
      productsResponse?.data?.products?.flatMap(({ productVariants, id }) =>
        productVariants.map((x) => ({ ...x, parentProductId: id }))
      ) ?? []
    );
  }, [productsResponse?.data?.products]);

  // const schemeType = appliedFilters?.listFilter?.[ListFilterKey.Schemes] || [];

  const onSelectProduct = (productId: number) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const fetchProducts = useCallback(
    (pageParams: { pageNo: number; pageSize: number }) => {
      if (pageParams.pageNo === 1 && !initialLoadRef.current) {
        return;
      }

      const categoryCodes = selectedCategories
        ?.map((i) => i?.categoryCode)
        .filter(Boolean);

      getProductsList({
        workspaceId: sellerWorkspaceId,
        params: pageParams,
        body: {
          ...(debouncedSearchValue ? { searchKey: debouncedSearchValue } : {}),
          sortBy: appliedFilters?.sortOptions?.field ?? 'sortOrder',
          sortDirection:
            appliedFilters?.sortOptions?.direction ?? SortDirection.ASCENDING,
          includeFacets: true,
          includeCategoryTreeList: true,
          ...(categoryCodes?.length > 0 ? { categoryCodes } : {}),
          statusFilter: 'ACTIVE',
          ...(selectedSegment.length > 0
            ? { segmentIds: [selectedSegment] }
            : {}),
        },
      });

      if (pageParams.pageNo === 1) {
        initialLoadRef.current = false;
      }
    },
    [
      debouncedSearchValue,
      selectedCategories,
      appliedFilters?.sortOptions,
      selectedSegment,
    ]
  );

  useEffect(() => {
    if (
      productsResponse?.data?.total &&
      totalRef.current !== productsResponse?.data?.total
    ) {
      totalRef.current = productsResponse.data.total;
    }
  }, [productsResponse?.data?.total]);

  const {
    data: productsData,
    pageNo,
    lastItemRef,
    onScroll,
    resetAll,
    refresh,
    isPageLoading,
    isEndOfList,
  } = usePaginatedList<ProductVariant>({
    fetchDataApiCall: fetchProducts,
    apiResponseData: memoizedProducts,
    isLoading: productsResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [
      debouncedSearchValue,
      selectedCategories,
      appliedFilters,
      selectedSegment,
    ],
    isError: productsResponse.isError,
    paginationMeta: {
      startRecord: productsResponse?.data?.startRecord || 0,
      endRecord: productsResponse?.data?.endRecord || 0,
      totalCount: productsResponse?.data?.total || 0,
    },
  });

  useEffect(() => {
    if (debouncedSearchValue?.length !== 0) {
      setSelectedCategories([]);
    }
  }, [debouncedSearchValue]);

  const onSearchCancel = () => {
    dispatch(removeSearchKey());
  };

  useEffect(() => {
    initialLoadRef.current = true;
  }, [
    debouncedSearchValue,
    selectedCategories,
    appliedFilters?.sortOptions,
    selectedSegment,
  ]);

  const toggleProductAvaiablity = async (product: ProductVariant) => {
    const { parentProductId, id, stock } = product;
    const updatedStock = !isNil(stock) && stock > 0 ? 0 : 1;
    const response = await updateProduct({
      workspaceId: sellerWorkspaceId,
      id: parentProductId,
      productVariants: [
        {
          id,
          stockOnHand: updatedStock,
          price: product.price,
        },
      ],
    }).unwrap();
    const isSuccess = response.message === 'success';
    if (isSuccess) {
      const index = productsData.findIndex((x) => x.id === id);
      productsData[index].stock = updatedStock;
    }
    return isSuccess;
  };

  const onSavePrices = () => {
    const productsToUpdate = Object.values(updatedProducts);
    const products = productsToUpdate.map(({ price, productId, pvId }) => ({
      id: productId,
      productVariants: [
        {
          id: pvId,
          price: price,
        },
      ],
    }));
    const selectedProductVariantIds = products?.map(
      (product) => product?.productVariants?.[0]?.id
    );
    const selectedProducts = productsData
      ?.filter((product) => selectedProductVariantIds?.includes(product?.id))
      ?.map((x) => {
        return {
          ...x,
          price:
            products?.find(
              (product) =>
                product?.productVariants?.[0]?.id === x?.productVariantId
            )?.productVariants?.[0]?.price || x?.price,
        };
      });
    dispatch(updateSelectedProducts(selectedProducts));
    dispatch(clearExtraDetails());
    buldUpdateProduct({ sellerWorkspaceId, items: products });
  };

  const onUpdatePrice = (item: PriceUpdatedProduct) => {
    const { price, pvId } = item;
    const id = String(pvId);
    if (!updatedProducts[id]) {
      setUpdatedProducts((prev) => ({ ...prev, [id]: item }));
    } else {
      setUpdatedProducts((prev) => ({
        ...prev,
        [id]: { ...prev[id], price },
      }));
    }
  };

  useEffect(() => {
    if (bulkUpdateProductResponse.isSuccess && bulkUpdateProductResponse.data) {
      setSuccessState(true);
      setUpdatedProducts({});
    } else if (bulkUpdateProductResponse.isError) {
      dispatch(
        showToast({
          message: 'Error updating prices',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'Danger',
        })
      );
    }
  }, [bulkUpdateProductResponse]);

  useEffect(() => {
    dispatch(updateSelectedProducts([]));
  }, []);

  useEffect(() => {
    const data: ChipDataItem[] =
      segmentFilters.length > 0 && segmentFilters[0].items.length > 1
        ? segmentFilters[0].items
        : [
            { id: '', selected: selectedSegment === '', value: 'All' },
            ...(productsResponse.data?.segmentsSummary || []).map(
              ({ segmentId, segmentName }) => ({
                id: segmentId,
                selected: false,
                value: segmentName,
              })
            ),
          ];
    const filters: ChipDataItem[] = data.map((x) => ({
      ...x,
      selected: selectedSegment === x.id,
    }));
    const filterData: ChipFilterSection[] = [
      {
        items: filters,
        onItemPress: ({ id }) => setSelectedSegment(id),
      },
    ];
    setSegmentFilters(filterData);
  }, [productsResponse.data?.segmentsSummary, selectedSegment]);

  const showImage = useMemo(
    () => catalogueView === CatalogueView.ListWithImagesView,
    [catalogueView]
  );

  return {
    productsData,
    total: totalRef.current ?? 0,
    pageNo,
    lastItemRef,
    onScroll,
    isPageLoading,
    resetAll,
    categoryTree,
    setSelectedCategories,
    onSearchCancel,
    searchKey,
    toggleProductAvaiablity,
    onSavePrices,
    onUpdatePrice,
    updatedProducts,
    updateLoading: updateProductResponse.isLoading,
    successState,
    setSuccessState,
    isLoading:
      pageNo === 1 &&
      (productsResponse?.isUninitialized || productsResponse?.isLoading),
    setShowFilter,
    showFilter,
    setShowSearch,
    showSearch,
    selectedProducts,
    onSelectProduct: onSelectProduct,
    setSelectedProducts,
    filterIndex,
    setFilterIndex,
    sharing,
    setSharing,
    segmentFilters,
    setSelectedSegment,
    isEndOfList,
    refreshProducts: refresh,
    showImage,
  };
};
